/**
 * OnboardingSecurity.jsx — v228.1.0 (Phase 1b)
 *
 * Step 2 of onboarding: two-factor sign-in setup, run IN-FLOW.
 *
 * Auth model (important):
 *   For a brand-new signup the patient reaches onboarding pre-authenticated,
 *   carrying only `pendingUserId` (set at phone verification). Basics (step 1)
 *   is in-memory and touches no PHI. THIS step is what grants the live
 *   session: `verify2faSetup` returns { token, user }, which we persist via
 *   setUser + localStorage. Every step after this runs authenticated.
 *
 *   If the patient is ALREADY authenticated when they hit this step (e.g.
 *   returning to finish onboarding, or social login), 2FA/PIN is already in
 *   place — we show a short "you're protected" confirmation and let them pass.
 *
 *   The legacy /security route (TwoFactorSetup) is preserved untouched as a
 *   fallback for the sign-in → needs2faSetup path.
 *
 * Mirrors the proven TwoFactorSetup logic (same authAPI calls, same QR via
 * api.qrserver.com, same setUser+token on success), restyled into the
 * onboarding system with authenticator-first + a copy-codes affordance.
 */
import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Shield, Smartphone, MessageSquare, Check, Copy, Eye, EyeOff, Lock, Delete, ScanFace } from 'lucide-react'
// s114 Face ID opt-in (Dr. Rami 2026-10-22, canvas F2 approved): offered right after the PIN is saved
import { biometricAvailable, verifyBiometric } from '../../utils/biometric'
import { saveFaceIdPref } from '../../utils/faceIdPref'
import { useI18n } from '../../i18n'
import { useUserStore } from '../../stores'
import { authAPI } from '../../services/api'
import { isSmsRefused, smsRefusedSentence } from '../../utils/smsRefusal'
import {
  OnboardingShell,
  ScreenHeader,
  ScreenIntro,
  PrimaryButton,
  Footer,
  ScrollBody,
  useScrollToTop,
  NAVY,
  BLUE,
  LIGHT_BLUE,
  ICE_BLUE,
  GREEN,
  PASTEL_GREEN,
  TEXT_MUTED,
  TEXT_HINT,
  BORDER_LIGHT,
} from './onboardingPrimitives'
import { QRCodeDisplay } from '../../utils/qrcode'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

const subVariants = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
}

const recPill = {
  fontSize: 10,
  fontWeight: 700,
  color: BLUE,
  background: ICE_BLUE,
  border: `1px solid ${LIGHT_BLUE}`,
  borderRadius: 999,
  padding: '2px 7px',
  letterSpacing: '0.02em',
}

function methodCard(primary) {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
    textAlign: 'left',
    padding: 16,
    borderRadius: 16,
    background: '#fff',
    border: `1.5px solid ${primary ? BLUE : '#E2E8F0'}`,
    cursor: 'pointer',
  }
}

function iconWrap(bg) {
  return {
    width: 40,
    height: 40,
    borderRadius: 11,
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }
}

function LoadingRow({ label }) {
  return (
    <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, color: '#475569', fontSize: 13 }}>
      <OrellaSpinner size={16} />
      {label}
    </div>
  )
}

export default function OnboardingSecurity({ onNext, onBack, onExit, greet = false }) {
  const { t } = useI18n()
  const pendingUserId = useUserStore((s) => s.pendingUserId)
  const user = useUserStore((s) => s.user)
  const isAuthenticated = useUserStore((s) => s.isAuthenticated)
  const setUser = useUserStore((s) => s.setUser)
  const userId = pendingUserId || user?.id

  useScrollToTop('security')

  // ★ s95c: a session is NOT proof of two-factor. An Apple/Google sign-in gets a session
  //   without 2FA while SSO_REQUIRE_2FA is off, and a re-sign-up after deleting the account
  //   (the server wipes totp_method) then read "already active" here — a false claim. So a
  //   signed-in patient is ASKED: the server's /auth/2fa/status decides 'secured' vs 'choose'.
  //   A failed check offers setup (never claims protection it could not confirm).
  const signedIn = isAuthenticated && !pendingUserId

  const [sub, setSub] = useState(signedIn ? 'checking' : 'choose') // checking | choose | totp-scan | confirm | sms-verify | backup-codes | secured
  const [method, setMethod] = useState('totp')
  const [qrUri, setQrUri] = useState('')
  const [secret, setSecret] = useState('')
  const [showSecret, setShowSecret] = useState(false)
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState([])
  const [copied, setCopied] = useState(false)
  const [secretCopied, setSecretCopied] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const inputRef = useRef(null)

  // v237.6.0 — session-PIN sub-stage. Kept INSIDE the Security step so the
  // onboarding step count stays at 8 (TOTAL_STEPS unchanged). Runs after the
  // session is granted (2FA verify) or for already-secured users.
  const [pinStage, setPinStage] = useState('enter') // enter | confirm
  const [pinDigits, setPinDigits] = useState('')
  const [pinFirst, setPinFirst] = useState('')
  const [pinError, setPinError] = useState('')
  const [pinSaving, setPinSaving] = useState(false)
  const [faceIdTurnedOn, setFaceIdTurnedOn] = useState(false) // s114 F2
  const alreadyHasPin = !!user?.sessionPinEnabled

  const pinDigit = (d) => {
    setPinError('')
    setPinDigits((prev) => (prev.length >= 4 ? prev : prev + d))
  }
  const pinBack = () => {
    setPinError('')
    setPinDigits((prev) => prev.slice(0, -1))
  }
  const pinContinue = async () => {
    if (pinDigits.length !== 4) return
    if (pinStage === 'enter') {
      setPinFirst(pinDigits)
      setPinDigits('')
      setPinStage('confirm')
      return
    }
    if (pinDigits !== pinFirst) {
      setPinError(t('onboarding.security.pinNoMatch', "PINs don't match. Try again."))
      setPinDigits('')
      setPinFirst('')
      setPinStage('enter')
      return
    }
    setPinSaving(true)
    try {
      await authAPI.setSessionPin(pinDigits)
      if (user) setUser({ ...user, sessionPinEnabled: true })
      // ★★ s114 F2: on a phone with Face ID set up, offer it now; anywhere else the flow is unchanged.
      if (await biometricAvailable()) { setPinSaving(false); setFaceIdTurnedOn(false); setSub('faceid'); return }
      onNext()
    } catch (e) {
      setPinError(e.error || t('onboarding.security.pinSaveFailed', 'Could not save PIN. Please try again.'))
      setPinSaving(false)
    }
  }

  useEffect(() => {
    if (sub !== 'checking') return
    let live = true
    authAPI.get2faStatus()
      .then((r) => {
        if (!live) return
        // ★ s98 (Dr. Rami, 2026-10-07): an Apple/Google-only account never gets 2FA —
        //   it sets a PIN and moves on. The server's isSsoOnly decides (the same answer
        //   securityPosture.js reads); an account with a password is still offered 2FA.
        if (r && r.isSsoOnly === true && r.enabled !== true) {
          if (alreadyHasPin) { onNext(); return }
          setPinStage('enter'); setPinDigits(''); setPinFirst(''); setPinError(''); setSub('set-pin')
          return
        }
        setSub(r && r.enabled === true ? 'secured' : 'choose')
      })
      .catch(() => { if (live) setSub('choose') })
    return () => { live = false }
  }, [sub])

  useEffect(() => {
    if (sub === 'confirm' || sub === 'sms-verify') {
      setTimeout(() => inputRef.current?.focus(), 120)
    }
  }, [sub])

  // v242.15.0 — auto-advance to the re-enter (confirm) stage the instant the 4th
  // digit lands on the first PIN entry, so the patient isn't stranded hunting for
  // Continue. Mirrors pinContinue's enter->confirm branch exactly; the short delay
  // lets the 4th dot fill before the switch. The re-entry still confirms via the
  // explicit Set PIN action, so this only removes the redundant first-stage tap.
  useEffect(() => {
    if (sub === 'set-pin' && pinStage === 'enter' && pinDigits.length === 4) {
      const id = setTimeout(() => {
        setPinFirst(pinDigits)
        setPinDigits('')
        setPinStage('confirm')
        setPinError('')
      }, 180)
      return () => clearTimeout(id)
    }
  }, [sub, pinStage, pinDigits])

  const chooseMethod = async (m) => {
    if (!userId) {
      setError(t('onboarding.security.noSession', 'Your session expired. Please sign in again.'))
      return
    }
    setMethod(m)
    setError('')
    setLoading(true)
    try {
      const r = await authAPI.setup2fa(userId, m)
      if (m === 'totp') {
        setQrUri(r.qrUri)
        setSecret(r.secret)
        setSub('totp-scan')
      } else {
        setCode('')
        setSub('sms-verify')
      }
    } catch (e) {
      // PROD-807: a refused number (sms_us_ca_only) sent nothing - show the server's sentence;
      // sub stays on the method choice.
      setError(isSmsRefused(e)
        ? (smsRefusedSentence(e) || t('onboarding.security.setupFailed', 'Could not start setup. Please try again.'))
        : (e.error || t('onboarding.security.setupFailed', 'Could not start setup. Please try again.')))
    } finally {
      setLoading(false)
    }
  }

  const verify = async () => {
    if (code.length !== 6) {
      setError(t('onboarding.security.enter6', 'Enter the 6-digit code.'))
      return
    }
    setError('')
    setLoading(true)
    try {
      const r = await authAPI.verify2faSetup(userId, code, method)
      // This grants the live session.
      if (r.token && r.user) {
        // v242.169.0 TOKEN-KEY-1 — see TwoFactorSetup: verify2faSetup has already
        // stored this token through setToken(). The raw duplicate written here
        // outlived every logout.
        setUser(r.user)
      }
      setBackupCodes(r.backupCodes || [])
      setSub('backup-codes')
    } catch (e) {
      setError(e.error || t('onboarding.security.invalidCode', "That code didn't match. Try again."))
      setCode('')
      inputRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  // Robust clipboard copy — navigator.clipboard isn't available in every
  // in-app/mobile browser, so fall back to a hidden textarea + execCommand.
  const copyText = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
        return true
      }
    } catch { /* fall through to legacy path */ }
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.setAttribute('readonly', '')
      ta.style.position = 'absolute'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch { return false }
  }

  const copyCodes = async () => {
    await copyText(backupCodes.join('\n'))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const copySecret = async () => {
    await copyText(secret)
    setSecretCopied(true)
    setTimeout(() => setSecretCopied(false), 2000)
  }

  const handleBack = () => {
    if (sub === 'totp-scan' || sub === 'sms-verify') {
      setSub('choose'); setError(''); setCode('')
    } else if (sub === 'confirm') {
      setSub('totp-scan'); setError(''); setCode('')
    } else if (sub === 'set-pin') {
      // PIN is required and the session is already granted — stay in-step.
      // Back only steps the confirm stage back to entry.
      if (pinStage === 'confirm') {
        setPinStage('enter'); setPinDigits(''); setPinFirst(''); setPinError('')
      }
    } else if (onBack) {
      onBack()
    }
  }

  const turnOnFaceId = async () => {
    const ok = await verifyBiometric(t('lockScreen.faceIdReason'))
    if (!ok) return
    saveFaceIdPref(user?.id, true)
    setFaceIdTurnedOn(true)
  }

  const dialogue =
    sub === 'faceid'
      ? t('faceId.offerBody')
      : sub === 'set-pin'
      ? t('onboarding.security.dialoguePin', 'Set a 4-digit PIN to get back in fast when you step away — without typing your full password each time.')
      : sub === 'backup-codes'
      ? t('onboarding.security.dialogueBackup', 'Save these backup codes somewhere safe — they let you in if you ever lose your phone.')
      : sub === 'secured'
      ? (greet
        ? t('onboarding.security.dialogueSecuredGreet', "Hi, I'm Orella. Your account's already protected with two-factor sign-in — you're all set here.")
        : t('onboarding.security.dialogueSecured', "Your account's already protected with two-factor sign-in — you're all set here."))
      : (greet
        ? t('onboarding.security.dialogueGreet', "Hi, I'm Orella. Let's protect your health information with two-factor sign-in. Pick how you'd like to confirm it's you each time.")
        : t('onboarding.security.dialogue', "Let's protect your health information with two-factor sign-in. Pick how you'd like to confirm it's you each time."))

  const title =
    sub === 'faceid'
      ? (faceIdTurnedOn ? t('faceId.isOn') : t('faceId.offerTitle'))
      : sub === 'set-pin'
      ? t('onboarding.security.titlePin', 'Create a quick-access PIN')
      : sub === 'backup-codes'
      ? t('onboarding.security.titleBackup', 'Account secured')
      : sub === 'secured'
      ? t('onboarding.security.titleSecured', "You're protected")
      : t('onboarding.security.title', 'Secure your account')

  return (
    <OnboardingShell>
      <ScreenHeader onBack={handleBack} step={0} onExit={onExit} />
      <ScreenIntro
        eyebrow={t('onboarding.security.eyebrow', 'Account security')}
        title={title}
        dialogue={dialogue}
      />

      <ScrollBody>
        {error && (
          <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', fontSize: 13 }}>
            {error}
          </div>
        )}

        <motion.div
          key={sub}
          initial={subVariants.initial}
          animate={subVariants.animate}
          transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {sub === 'choose' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button onClick={() => chooseMethod('totp')} disabled={loading} style={methodCard(true)}>
                <div style={iconWrap(ICE_BLUE)}><Smartphone size={20} color={BLUE} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: NAVY }}>
                      {t('onboarding.security.authenticatorApp', 'Authenticator app')}
                    </span>
                    <span style={recPill}>{t('onboarding.security.recommended', 'Recommended')}</span>
                  </div>
                  <p style={{ fontSize: 13, color: TEXT_MUTED, marginTop: 3, lineHeight: 1.45 }}>
                    {t('onboarding.security.totpDesc', 'Google Authenticator, Authy, or similar — the most secure option.')}
                  </p>
                </div>
              </button>

              <button onClick={() => chooseMethod('sms')} disabled={loading} style={methodCard(false)}>
                <div style={iconWrap('#F2F4F7')}><MessageSquare size={20} color={TEXT_MUTED} /></div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: NAVY }}>
                    {t('onboarding.security.textMessage', 'Text message code')}
                  </span>
                  <p style={{ fontSize: 13, color: TEXT_MUTED, marginTop: 3, lineHeight: 1.45 }}>
                    {t('onboarding.security.smsDesc', 'We text a 6-digit code each time you sign in. No extra app needed.')}
                  </p>
                </div>
              </button>

              {loading && <LoadingRow label={t('onboarding.security.settingUp', 'Setting up…')} />}
            </div>
          )}

          {sub === 'totp-scan' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* ★★★ v242.87.0 — TWO defects in these seven lines.
                  (1) SECURITY, unreported and the more serious of the two: this
                      rendered the QR by handing `qrUri` to api.qrserver.com in a
                      URL query string. `qrUri` is the otpauth:// URI, which
                      CONTAINS THE TOTP SHARED SECRET. Every 2FA setup this app
                      has ever done transmitted the second factor to a third
                      party with no BAA, in plaintext, in a GET. `qrcode` has been
                      a dependency this whole time and QRCodeDisplay already wraps
                      it, so the fix is an import. TwoFactorSetup.jsx had the
                      identical line and is fixed in the same pass.
                  (2) TF b35 #24: "the auth shows QR code but I can't scan the
                      code because I use it" — i.e. the phone displaying the QR is
                      the phone that would scan it. The only working path was
                      Show setup key -> copy -> leave the app -> paste, and
                      nothing on screen said so. iOS and Android both route the
                      otpauth: scheme to a registered authenticator, and the URI
                      was already sitting in state — it just was never offered as
                      something you could tap. */}
              {qrUri && (
                <div style={{ background: '#fff', padding: 14, borderRadius: 16, border: `1px solid ${BORDER_LIGHT}`, marginBottom: 14 }}>
                  <QRCodeDisplay value={qrUri} size={188} color={NAVY} />
                </div>
              )}
              {qrUri && (
                <a
                  href={qrUri}
                  data-otpauth-link="1"
                  style={{ color: BLUE, fontSize: 13.5, fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10 }}
                >
                  {t('onboarding.security.openInApp', 'Setting up on this phone? Open my authenticator app')}
                </a>
              )}
              <button
                onClick={() => setShowSecret(!showSecret)}
                style={{ background: 'none', border: 'none', color: BLUE, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10 }}
              >
                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                {showSecret ? t('onboarding.security.hideKey', 'Hide setup key') : t('onboarding.security.showKey', 'Show setup key')}
              </button>
              {showSecret && secret && (
                <div style={{ background: '#F8FAFC', padding: '8px 12px', borderRadius: 10, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <code style={{ fontFamily: 'monospace', fontSize: 13, color: NAVY, userSelect: 'all' }}>{secret}</code>
                  <button
                    onClick={copySecret}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: secretCopied ? GREEN : TEXT_HINT, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    aria-label={t('onboarding.security.copyKey', 'Copy setup key')}
                  >
                    {secretCopied ? <Check size={14} /> : <Copy size={14} />}
                    {secretCopied && <span style={{ fontSize: 12, fontWeight: 600 }}>{t('onboarding.security.copied', 'Copied')}</span>}
                  </button>
                </div>
              )}
              <p style={{ fontSize: 13, color: TEXT_HINT, textAlign: 'center', lineHeight: 1.5 }}>
                {t('onboarding.security.scanHint', 'Scan the code with another device — or tap the link above if your authenticator app is on this phone. Then tap Continue and enter the 6-digit code it shows.')}
              </p>
            </div>
          )}

          {(sub === 'confirm' || sub === 'sms-verify') && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <p style={{ fontSize: 14, color: TEXT_MUTED, lineHeight: 1.5, textAlign: 'center', marginBottom: 16 }}>
                {sub === 'confirm'
                  ? t('onboarding.security.enterTotp', 'Enter the 6-digit code from your authenticator app.')
                  : t('onboarding.security.enterSms', 'Enter the 6-digit code we just texted you.')}
              </p>
              <input
                ref={inputRef}
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError('') }}
                inputMode="numeric"
                placeholder="••••••"
                style={{ width: '100%', maxWidth: 220, textAlign: 'center', letterSpacing: '0.4em', fontSize: 24, fontWeight: 600, color: NAVY, padding: '12px 0', border: `1.5px solid ${BORDER_LIGHT}`, borderRadius: 12, outline: 'none' }}
              />
            </div>
          )}

          {sub === 'backup-codes' && (
            <div>
              <div style={{ background: ICE_BLUE, border: `1px solid ${LIGHT_BLUE}`, borderRadius: 14, padding: 16, textAlign: 'center', fontFamily: 'monospace', fontSize: 14, letterSpacing: '0.04em', lineHeight: 2.1, color: NAVY }}>
                {backupCodes.length > 0
                  ? backupCodes.map((c, i) => <div key={i}>{c}</div>)
                  : <div style={{ color: TEXT_MUTED }}>{t('onboarding.security.noBackup', 'No backup codes were returned.')}</div>}
              </div>
              {backupCodes.length > 0 && (
                <button
                  onClick={copyCodes}
                  style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 16px', borderRadius: 999, background: copied ? PASTEL_GREEN + '40' : '#fff', border: `1.5px solid ${copied ? GREEN : LIGHT_BLUE}`, color: copied ? '#0F6E56' : BLUE, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
                >
                  {copied ? <Check size={15} /> : <Copy size={15} />}
                  {copied ? t('onboarding.security.copied', 'Copied') : t('onboarding.security.copyCodes', 'Copy codes')}
                </button>
              )}
            </div>
          )}

          {sub === 'checking' && (
            <p role="status" style={{ fontSize: 14, color: TEXT_MUTED, textAlign: 'center', padding: '24px 0' }}>
              {t('common.loading', 'Loading...')}
            </p>
          )}

          {sub === 'secured' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0' }}>
              <div style={{ width: 64, height: 64, borderRadius: 999, background: PASTEL_GREEN + '40', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <Shield size={30} color={GREEN} />
              </div>
              <p style={{ fontSize: 14.5, color: NAVY, textAlign: 'center', lineHeight: 1.5 }}>
                {t('onboarding.security.securedBody', 'Two-factor sign-in is already active on your account.')}
              </p>
            </div>
          )}

          {sub === 'faceid' && (
            <div data-faceid-offer style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, padding: '8px 0' }}>
              <div style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #BBF7D0', background: '#F0FDF4', borderRadius: 14, padding: '12px 14px', fontSize: 14, fontWeight: 700, color: '#166534' }}>
                <Check size={18} color="#166534" />
                {t('security.pinSavedMsg')}
              </div>
              <div style={{ width: 88, height: 88, borderRadius: 24, background: faceIdTurnedOn ? PASTEL_GREEN + '40' : ICE_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {faceIdTurnedOn ? <Check size={40} color={GREEN} /> : <ScanFace size={44} color={BLUE} />}
              </div>
            </div>
          )}

          {sub === 'set-pin' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: TEXT_HINT, margin: '0 0 14px' }}>
                {pinStage === 'enter'
                  ? t('onboarding.security.choosePin', 'Choose your PIN')
                  : t('onboarding.security.confirmPin', 'Re-enter your PIN')}
              </p>

              <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
                {[0, 1, 2, 3].map((i) => {
                  const filled = i < pinDigits.length
                  const isConfirm = pinStage === 'confirm'
                  return (
                    <div
                      key={i}
                      style={{
                        width: 46,
                        height: 54,
                        borderRadius: 13,
                        border: `2px solid ${filled ? (isConfirm ? GREEN : BLUE) : '#E2E8F0'}`,
                        background: filled ? (isConfirm ? PASTEL_GREEN : ICE_BLUE) : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 22,
                        color: NAVY,
                      }}
                    >
                      {filled ? '●' : ''}
                    </div>
                  )
                })}
              </div>

              {pinError && <p style={{ color: '#DC2626', fontSize: 13, margin: '8px 0 0', textAlign: 'center' }}>{pinError}</p>}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 9, maxWidth: 230, width: '100%', margin: '20px auto 0' }}>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back'].map((key, i) => {
                  if (key === '') return <div key={i} />
                  if (key === 'back') {
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={pinBack}
                        style={{ height: 50, borderRadius: 13, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        aria-label="Backspace"
                      >
                        <Delete size={20} color={TEXT_HINT} />
                      </button>
                    )
                  }
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => pinDigit(key)}
                      disabled={pinSaving}
                      style={{ height: 50, borderRadius: 13, border: '1px solid #E2E8F0', background: '#fff', fontFamily: 'inherit', fontSize: 19, fontWeight: 600, color: NAVY, cursor: 'pointer' }}
                    >
                      {key}
                    </button>
                  )
                })}
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: '#F8FAFC', border: `1px solid ${BORDER_LIGHT}`, borderRadius: 14, padding: '13px 14px', marginTop: 20 }}>
                <Lock size={16} color={BLUE} style={{ flexShrink: 0, marginTop: 1 }} />
                <p style={{ fontSize: 12, lineHeight: 1.5, color: TEXT_MUTED, margin: 0 }}>
                  {t('onboarding.security.pinWhy', 'If you leave Orella and come back, your health info stays locked behind this PIN. Your session stays valid for 30 minutes — after that you’ll sign in fully.')}
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </ScrollBody>

      <Footer>
        {sub === 'totp-scan' && (
          <PrimaryButton onClick={() => { setSub('confirm'); setCode('') }}>
            {t('common.continue', 'Continue')}
          </PrimaryButton>
        )}
        {(sub === 'confirm' || sub === 'sms-verify') && (
          <PrimaryButton onClick={verify} disabled={code.length !== 6 || loading}>
            {loading ? t('onboarding.security.verifying', 'Verifying…') : t('onboarding.security.verify', 'Verify')}
          </PrimaryButton>
        )}
        {sub === 'backup-codes' && (
          <PrimaryButton onClick={() => { setPinStage('enter'); setPinDigits(''); setPinFirst(''); setPinError(''); setSub('set-pin') }}>
            {t('onboarding.security.savedContinue', "I've saved them — continue")}
          </PrimaryButton>
        )}
        {sub === 'secured' && (
          <PrimaryButton onClick={() => { if (alreadyHasPin) { onNext() } else { setPinStage('enter'); setPinDigits(''); setPinFirst(''); setPinError(''); setSub('set-pin') } }}>
            {t('common.continue', 'Continue')}
          </PrimaryButton>
        )}
        {sub === 'faceid' && !faceIdTurnedOn && (
          <>
            <PrimaryButton onClick={turnOnFaceId}>
              {t('faceId.turnOn')}
            </PrimaryButton>
            <button type="button" data-faceid-not-now onClick={() => onNext()} style={{ display: 'block', width: '100%', height: 44, marginTop: 6, border: 'none', background: 'transparent', color: BLUE, fontFamily: 'inherit', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              {t('faceId.notNow')}
            </button>
          </>
        )}
        {sub === 'faceid' && faceIdTurnedOn && (
          <PrimaryButton onClick={() => onNext()}>
            {t('common.continue', 'Continue')}
          </PrimaryButton>
        )}
        {sub === 'set-pin' && (
          <PrimaryButton onClick={pinContinue} disabled={pinDigits.length !== 4 || pinSaving}>
            {pinSaving
              ? t('onboarding.security.pinSaving', 'Saving…')
              : pinStage === 'enter'
              ? t('common.continue', 'Continue')
              : t('onboarding.security.setPin', 'Set PIN')}
          </PrimaryButton>
        )}
      </Footer>
    </OnboardingShell>
  )
}
